import {Box, Heading, Show, Stack, VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {buildArray, EMPTY_PLACEHOLDER} from 'shared/web';

import {Carousel} from '../../components/Carousel/Carousel';
import {CommentExpandable} from '../../components/CommentExpandable/CommentExpandable';
import {
  DocumentationAttribute,
  DocumentationItem,
} from '../../components/DocumentationItem/DocumentationItem';
import {Section} from '../../components/Section/Section';
import {Separator} from '../../components/Separator/Separator';
import {DocumentationDataBundle} from '../../hooks/useGetDataForDocumentation';
import i18n from '../../i18n/i18n';
import {isCebiaWorkspace} from '../../utils/isCebiaWorkspace';
import {isFieldVisible} from '../../utils/isFieldVisible';

interface DocumentationProps extends Omit<DocumentationDataBundle, 'sectionConfig'> {
  showEmptyFields: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export const Documentation = ({
  documentationSlideshow,
  documentationItemParams,
  documentation,
  firstRegistration,
  dateOfManufacture,
  showEmptyFields,
}: DocumentationProps) => {
  if (!documentation || !documentationItemParams) {
    return null;
  }

  return (
    <Section
      id="VEHICLE_DOCUMENTATION"
      icon="documentation"
      heading={i18n.t('documentationHeader')}
      content={
        <VStack spacing={[3, 6, 6, 10]}>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.vin?.isEnabledInAudit,
              value: documentation.vin?.value,
            })}
          >
            <DocumentationItem
              heading={i18n.t('vin')}
              attributes={[{label: 'VIN', value: documentation.vin?.value ?? EMPTY_PLACEHOLDER}]}
              comment={documentation.vin?.comment}
            />
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.technicalCert?.isEnabledInAudit,
              value: documentation.technicalCert?.status,
            })}
          >
            <DocumentationItem
              heading={i18n.t('technicalCertificate')}
              comment={documentation.technicalCert?.comment}
              attributes={documentationItemParams.technicalCertification}
            />
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.dateOfManufacture?.isEnabledInAudit,
              value: documentation.dateOfManufacture?.value,
            })}
          >
            <DocumentationItem
              heading={i18n.t('dateOfManufacture')}
              attributes={[
                {
                  label: i18n.t('date'),
                  value: dateOfManufacture,
                },
              ]}
              comment={documentation.dateOfManufacture?.comment}
            />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.firstRegistration?.isEnabledInAudit,
              value: documentation.firstRegistration?.value,
            })}
          >
            <DocumentationItem
              heading={i18n.t('firstRegistration')}
              attributes={[
                {
                  label: i18n.t('date'),
                  value: firstRegistration,
                },
              ]}
              comment={documentation.firstRegistration?.comment}
            />
          </Show>
          <Show
            when={
              isFieldVisible({
                showEmptyFields,
                isEnabledInAudit: documentation.dateOfManufacture?.isEnabledInAudit,
                value: documentation.dateOfManufacture?.value,
              }) ||
              isFieldVisible({
                showEmptyFields,
                isEnabledInAudit: documentation.firstRegistration?.isEnabledInAudit,
                value: documentation.firstRegistration?.value,
              })
            }
          >
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.cocListStatus.isEnabledInAudit,
              value: documentation.cocListStatus.value,
            })}
          >
            <DocumentationItem
              heading={i18n.t('cocList')}
              attributes={[
                {
                  label: i18n.t('status'),
                  value: documentation.cocListStatus.value ?? EMPTY_PLACEHOLDER,
                },
              ]}
            />
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.vehicleUnderFactoryWarranty?.isEnabledInAudit,
              value: documentation.vehicleUnderFactoryWarranty?.status,
            })}
          >
            <DocumentationItem
              heading={i18n.t('vehicleUnderFactoryWarranty')}
              attributes={documentationItemParams.vehicleUnderFactoryWarranty}
            />
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.serviceBookState?.isEnabledInAudit,
              value: documentation.serviceBookType ?? documentation.serviceBookState?.value,
            })}
          >
            <DocumentationItem
              heading={i18n.t('serviceBook')}
              attributes={buildArray<DocumentationAttribute>()
                .when(
                  isFieldVisible({
                    showEmptyFields,
                    isEnabledInAudit: documentation.serviceBookState?.isEnabledInAudit,
                    value: documentation.serviceBookType,
                  }),
                  {
                    label: i18n.t('type'),
                    value: documentation.serviceBookType ?? EMPTY_PLACEHOLDER,
                  }
                )
                .when(
                  isFieldVisible({
                    showEmptyFields,
                    isEnabledInAudit: documentation.serviceBookState?.isEnabledInAudit,
                    value: documentation.serviceBookState?.value,
                  }),
                  {
                    label: i18n.t('status'),
                    value: documentation.serviceBookState?.value ?? EMPTY_PLACEHOLDER,
                  }
                )}
              comment={documentation.serviceBookState?.comment}
            />
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.currentMileage?.isEnabledInAudit,
              value: documentation.currentMileage?.value?.value?.toString(),
            })}
          >
            <DocumentationItem
              heading={i18n.t('currentMileage')}
              attributes={[
                {label: i18n.t('mileage'), value: documentationItemParams.currentMileage},
              ]}
              comment={documentation.currentMileage?.comment}
            />
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentationItemParams.operatingInstructions.isEnabledInAudit,
              value: documentation.operatingInstructions,
            })}
          >
            <DocumentationItem
              heading={i18n.t('operatingInstructions')}
              attributes={documentationItemParams.operatingInstructions.attributes}
            />
            <Separator />
          </Show>
          <Show
            when={
              isFieldVisible({
                showEmptyFields,
                isEnabledInAudit:
                  documentation.vehicleModifiedAgainstProductionTuning?.isEnabledInAudit,
                value: documentation.vehicleModifiedAgainstProductionTuning?.value,
              }) && isCebiaWorkspace()
            }
          >
            <DocumentationItem
              heading={i18n.t('vehicleModifiedAgainstProductionTuning')}
              attributes={[
                {
                  label: '',
                  value: documentation.vehicleModifiedAgainstProductionTuning?.value,
                },
              ]}
              comment={documentation.vehicleModifiedAgainstProductionTuning?.comment}
            />
            <Separator />
          </Show>
          <Show
            when={isFieldVisible({
              showEmptyFields,
              isEnabledInAudit: documentation.mandatoryEquipmentAndAccessories?.isEnabledInAudit,
              value: documentation.mandatoryEquipmentAndAccessories?.value,
            })}
          >
            <Stack
              direction={['column', 'row', 'row', 'row']}
              spacing={[3, 6, 10, 10]}
              align="flex-start"
            >
              <Box flex={1} maxWidth={100} width="100%">
                <Heading size={2}>{i18n.t('mandatoryEquipmentAndAccessories')}</Heading>
              </Box>
              <Box flex={1} width="100%">
                <VStack spacing={[3, 6, 6, 10]}>
                  <Box>
                    <Heading size={3} alternative>
                      {documentation.mandatoryEquipmentAndAccessories?.value ?? EMPTY_PLACEHOLDER}
                    </Heading>
                  </Box>
                  <Show
                    when={isNotNilOrEmpty(documentation.mandatoryEquipmentAndAccessories?.comment)}
                  >
                    <CommentExpandable
                      comment={documentation.mandatoryEquipmentAndAccessories?.comment}
                    />
                  </Show>
                </VStack>
              </Box>
            </Stack>
            <Separator />
          </Show>
          <Show when={isNotNilOrEmpty(documentationSlideshow)}>
            <Carousel isFilterOfDamageDisabled data={documentationSlideshow} />
          </Show>
        </VStack>
      }
    />
  );
};
